import { assetLibrary, assetGroups } from './assetLibrary';

// Combined lookup array — assetLibrary has single-URL items, assetGroups has multi-file assets (platforms, characters, pipes)
const allAssets = [...assetLibrary, ...assetGroups];

// Sanity checks — catch stale builds or broken imports immediately
const _counts = {
  platform: allAssets.filter(a => a.category === 'platform').length,
  player: allAssets.filter(a => a.category === 'player').length,
  pipe: allAssets.filter(a => a.category === 'pipe').length,
  background: allAssets.filter(a => a.category === 'background').length,
  obstacle: allAssets.filter(a => a.category === 'obstacle').length,
  collectible: allAssets.filter(a => a.category === 'collectible').length,
};
const _minimums = { platform: 15, player: 10, pipe: 10, background: 20, obstacle: 15, collectible: 8 };
for (const [cat, min] of Object.entries(_minimums)) {
  if (_counts[cat] < min) {
    console.error(`[ASSET LIBRARY BUG] Only ${_counts[cat]} ${cat} assets loaded (expected ${min}+). Stale build or broken import — AI asset resolution will fail. Run: npm run build`);
  }
}
if (!assetGroups || assetGroups.length === 0) {
  console.error('[ASSET LIBRARY BUG] assetGroups is empty — platforms, characters, and pipes will not resolve. Rebuild required.');
}

// Fallback IDs when AI-generated ID doesn't resolve — ensures games are always playable
const FALLBACK_IDS = {
  platform: 'platform-procedural-grass',
  pipe: 'pipe-procedural-industrial',
  character: 'new-characters-svg-beige',
  collectible: 'kenney-platformer-pack-collectible-star',
  obstacle: 'kenney-obstacle-spikes',
};

// Curated preset → best matching assets (used when user clicks a preset, no API call)
export const PRESET_ASSET_OVERRIDES = {
  // Retro/Gaming
  // Retro/Gaming
  'preset-arcade':       { backgroundId: 'proc-neon-district',     platformId: 'platform-procedural-neonblue',   pipeId: 'pipe-procedural-neon' },
  'preset-gameboy':      { backgroundId: 'proc-pixel-rain',        platformId: 'platform-procedural-neonfloat',  pipeId: 'pipe-procedural-neon' },
  'preset-terminal':     { backgroundId: 'proc-neon-grid',         platformId: 'platform-procedural-neonfloat',  pipeId: 'pipe-procedural-neon' },
  'preset-atari':        { backgroundId: 'proc-pixel-rain',        platformId: 'platform-procedural-laserred',   pipeId: 'pipe-procedural-neon' },
  'preset-synthwave':    { backgroundId: 'proc-neon-district',     platformId: 'platform-procedural-neonpurple', pipeId: 'pipe-procedural-neon' },
  'preset-vaporwave':    { backgroundId: 'proc-miami-vice',         platformId: 'platform-procedural-neonpink',   pipeId: 'pipe-procedural-neon' },

  // Playful
  'preset-comic':        { backgroundId: 'proc-candy-sky',         platformId: 'platform-procedural-confetti',   pipeId: 'pipe-procedural-candy' },
  'preset-candy':        { backgroundId: 'proc-candy-sky',         platformId: 'platform-procedural-rainbow',    pipeId: 'pipe-procedural-candy' },
  'preset-sports':       { backgroundId: 'proc-rooftop-sunset',    platformId: 'platform-procedural-stadium',    pipeId: 'pipe-procedural-industrial' },
  'preset-festival':     { backgroundId: 'proc-candy-sky',         platformId: 'platform-procedural-confetti',   pipeId: 'pipe-procedural-candy' },

  // Professional
  'preset-smilesflow-default': { backgroundId: 'proc-sky-clouds',  platformId: 'platform-procedural-grass',      pipeId: 'pipe-procedural-emerald' },
  'preset-corporate':    { backgroundId: 'proc-sky-clouds',        platformId: 'platform-procedural-grass',      pipeId: 'pipe-procedural-industrial' },
  'preset-tech':         { backgroundId: 'proc-neon-grid',         platformId: 'platform-procedural-hologram',   pipeId: 'pipe-procedural-neon' },
  'preset-clean':        { backgroundId: 'proc-sky-clouds',        platformId: 'platform-procedural-magmaledge', pipeId: 'pipe-procedural-ice' },
  'preset-ink':          { backgroundId: 'proc-downtown-night',    platformId: 'platform-procedural-thinline',   pipeId: 'pipe-procedural-industrial' },
  'preset-dark-modern':  { backgroundId: 'proc-downtown-night',    platformId: 'platform-procedural-concrete',   pipeId: 'pipe-procedural-industrial' },

  // Elegant
  'preset-gold':         { backgroundId: 'proc-warm-interior',     platformId: 'platform-procedural-neongold',   pipeId: 'pipe-procedural-gold' },
  'preset-rosegold':     { backgroundId: 'proc-warm-interior',     platformId: 'platform-procedural-neongold',   pipeId: 'pipe-procedural-gold' },
  'preset-marble':       { backgroundId: 'proc-crystal-cavern',    platformId: 'platform-procedural-magmaledge', pipeId: 'pipe-procedural-ice' },
  'preset-midnight':     { backgroundId: 'proc-night-stars',       platformId: 'platform-procedural-neonpurple', pipeId: 'pipe-procedural-neon' },
  'preset-aurora':       { backgroundId: 'proc-night-stars',       platformId: 'platform-procedural-icicle',     pipeId: 'pipe-procedural-ice' },

  // Vibrant/Nature
  'preset-sunset':       { backgroundId: 'proc-sunset-horizon',    platformId: 'platform-procedural-crag',       pipeId: 'pipe-procedural-terracotta' },
  'preset-jungle':       { backgroundId: 'proc-forest-canopy',     platformId: 'platform-procedural-grass',      pipeId: 'pipe-procedural-bamboo' },
  'preset-earth':        { backgroundId: 'proc-rolling-hills',     platformId: 'platform-procedural-grass',      pipeId: 'pipe-procedural-wood' },
  'preset-ocean':        { backgroundId: 'proc-ocean-waves',       platformId: 'platform-procedural-iceshelf',   pipeId: 'pipe-procedural-coral' },
  'preset-desert':       { backgroundId: 'proc-terracotta-desert', platformId: 'platform-procedural-crag',       pipeId: 'pipe-procedural-terracotta' },
  'preset-forest':       { backgroundId: 'proc-forest-canopy',     platformId: 'platform-procedural-grass',      pipeId: 'pipe-procedural-emerald' },
  'preset-terracotta':   { backgroundId: 'proc-terracotta-desert', platformId: 'platform-procedural-castle',     pipeId: 'pipe-procedural-brick' },
  'preset-y2k':          { backgroundId: 'proc-neon-grid',         platformId: 'platform-procedural-neonfloat',  pipeId: 'pipe-procedural-neon' },
  'preset-blush':        { backgroundId: 'proc-candy-sky',         platformId: 'platform-procedural-neonpink',   pipeId: 'pipe-procedural-candy' },
  'preset-miami-vice':   { backgroundId: 'proc-miami-vice',        platformId: 'platform-procedural-neonpink',   pipeId: 'pipe-procedural-neon' },

  // Seasonal
  'preset-christmas':    { backgroundId: 'proc-snowfall',          platformId: 'platform-procedural-icicle',     pipeId: 'pipe-procedural-ice' },
  'preset-halloween':    { backgroundId: 'proc-enchanted-twilight', platformId: 'platform-procedural-magmaledge', pipeId: 'pipe-procedural-lava' },
  'preset-summer':       { backgroundId: 'proc-sunset-horizon',    platformId: 'platform-procedural-wooddeck',   pipeId: 'pipe-procedural-coral' },
  'preset-spring':       { backgroundId: 'proc-bamboo-grove',      platformId: 'platform-procedural-grass',      pipeId: 'pipe-procedural-bamboo' },
};

// Game type → which asset categories are relevant
const GAME_ASSET_NEEDS = {
  snake: ['background', 'collectible', 'snakeSkin'],
  runner: ['background', 'character', 'collectible', 'obstacle'],
  sidescroller: ['background', 'platform', 'character', 'collectible', 'obstacle'],
  verticalclimber: ['background', 'platform', 'character', 'collectible', 'obstacle'],
  flappy: ['background', 'pipe', 'character', 'collectible'],
  quizrush: ['background'],
  minigolf: ['background'],
  stacker: ['background'],
};

/**
 * Convert asset IDs → campaign option fields by looking up URLs in assetLibrary.
 * Works for both preset overrides and AI-generated asset selections.
 *
 * @param {{ backgroundId?: string, platformId?: string, pipeId?: string, characterId?: string, collectibleId?: string, obstacleId?: string, snakeSkin?: string }} assetIds
 * @param {string} gameType
 * @returns {object} Campaign option fields (backgroundImage, platformImageLeft, pipeImageBody, playerAnimationFrames, etc.)
 */
export function resolveAssetIds(assetIds, gameType) {
  if (!assetIds) return {};
  const needs = GAME_ASSET_NEEDS[gameType] || ['background'];
  const result = {};

  if (needs.includes('background') && assetIds.backgroundId) {
    const bg = allAssets.find(a => a.id === assetIds.backgroundId && a.category === 'background');
    if (bg) {
      result.backgroundImage = bg.url;
      result.backgroundImageName = bg.name;
    }
  }
  if (needs.includes('platform') && assetIds.platformId) {
    const plat = allAssets.find(a => a.id === assetIds.platformId && a.category === 'platform')
      || allAssets.find(a => a.id === FALLBACK_IDS.platform && a.category === 'platform');
    if (!allAssets.find(a => a.id === assetIds.platformId && a.category === 'platform')) {
      console.warn(`[resolveAssetIds] Platform "${assetIds.platformId}" not found, falling back to "${FALLBACK_IDS.platform}"`);
    }
    if (plat?.assets) {
      result.platformImageLeft = plat.assets.platformImageLeft;
      result.platformImageMiddle = plat.assets.platformImageMiddle;
      result.platformImageRight = plat.assets.platformImageRight;
      result.platformSetName = plat.name;
    }
  }
  if (needs.includes('pipe')) {
    // If AI returned null for pipeId but game needs pipes, use fallback
    if (!assetIds.pipeId) assetIds = { ...assetIds, pipeId: FALLBACK_IDS.pipe };
  }
  if (needs.includes('pipe') && assetIds.pipeId) {
    const pipe = allAssets.find(a => a.id === assetIds.pipeId && a.category === 'pipe')
      || allAssets.find(a => a.id === FALLBACK_IDS.pipe && a.category === 'pipe');
    if (!allAssets.find(a => a.id === assetIds.pipeId && a.category === 'pipe')) {
      console.warn(`[resolveAssetIds] Pipe "${assetIds.pipeId}" not found, falling back to "${FALLBACK_IDS.pipe}"`);
    }
    if (pipe?.assets) {
      result.pipeImageBody = pipe.assets.pipeImageBody;
      result.pipeImageEnd = pipe.assets.pipeImageEnd;
      result.pipeSetName = pipe.name;
    }
  }
  if (needs.includes('character') && assetIds.characterId) {
    const char = allAssets.find(a => a.id === assetIds.characterId && a.category === 'player')
      || allAssets.find(a => a.id === FALLBACK_IDS.character && a.category === 'player');
    if (!allAssets.find(a => a.id === assetIds.characterId && a.category === 'player')) {
      console.warn(`[resolveAssetIds] Character "${assetIds.characterId}" not found, falling back to "${FALLBACK_IDS.character}"`);
    }
    if (char?.assets) {
      if (char.assets.playerAnimationFrames) result.playerAnimationFrames = char.assets.playerAnimationFrames;
      result.playerJumpImage = char.assets.playerJumpImage || null;
      result.playerSetName = char.name;
    } else if (char?.url) {
      // Single-URL character (e.g. front-facing portraits used by
      // verticalclimber). Game reads `playerImage`, not animation frames.
      result.playerImage = char.url;
      result.playerSetName = char.name;
    }
  }
  if (needs.includes('collectible') && assetIds.collectibleId) {
    const coll = allAssets.find(a => a.id === assetIds.collectibleId && a.category === 'collectible');
    if (coll?.url) {
      result.collectibleImage = coll.url;
      result.collectibleImageName = coll.name;
    }
  }
  if (needs.includes('obstacle') && assetIds.obstacleId) {
    const obs = allAssets.find(a => a.id === assetIds.obstacleId && a.category === 'obstacle');
    if (obs?.url) {
      result.obstacleImage = obs.url;
      result.obstacleImageName = obs.name;
    }
  }
  if (needs.includes('snakeSkin') && assetIds.snakeSkin) {
    result.snakeSkin = {
      mode: 'simple',
      simple: { theme: assetIds.snakeSkin },
    };
  }
  return result;
}
